/**
 * CurrencyInput renders an input field that format it's value according to currency formatting rules
 * onFocus: renders given value in unformatted manner: "9999,99"
 * onBlur: formats the given input: "9 999,99 €"
 */
import React, { useCallback, useEffect, useState } from 'react';
import { bool, func, number, object, oneOfType, shape, string } from 'prop-types';
import { Field } from 'react-final-form';
import classNames from 'classnames';
import Decimal from 'decimal.js';

import { intlShape, injectIntl } from '../../util/reactIntl';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  isSafeNumber,
  unitDivisor,
  convertUnitToSubUnit,
  convertMoneyToNumber,
  ensureDotSeparator,
  ensureSeparator,
  truncateToSubUnitPrecision,
} from '../../util/currency';
import { propTypes } from '../../util/types';
import * as log from '../../util/log';

import { ValidationError } from '../../components';

import css from './FieldCurrencyInput.module.css';

const { Money } = sdkTypes;

const allowedInputProps = allProps => {
  const { currencyConfig, defaultValue, intl, input, meta, ...inputProps } = allProps;
  return inputProps;
};

const getPrice = (unformattedValue, currencyConfig) => {
  const isEmptyString = unformattedValue === '';
  try {
    return isEmptyString
      ? null
      : new Money(
          convertUnitToSubUnit(unformattedValue, unitDivisor(currencyConfig.currency)),
          currencyConfig.currency
        );
  } catch (e) {
    return null;
  }
};

const CurrencyInputComponent = props => {
  const { currencyConfig, defaultValue, input, intl, className, placeholder, checkCase } = props;
  const initialValueIsMoney = input.value instanceof Money;

  if (initialValueIsMoney && input.value.currency !== currencyConfig.currency) {
    const e = new Error('Value currency different from marketplace currency');
    log.error(e, 'currency-input-invalid-currency', { currencyConfig, inputValue: input.value });
    throw e;
  }
  const initialValue = initialValueIsMoney ? convertMoneyToNumber(input.value) : defaultValue;
  const hasInitialValue = typeof initialValue === 'number' && !isNaN(initialValue);

  const testSubUnitFormat = intl.formatNumber('1.1', currencyConfig);
  const usesComma = testSubUnitFormat.indexOf(',') >= 0;

  const initialFormattedValue = hasInitialValue
    ? intl.formatNumber(
        ensureDotSeparator(
          truncateToSubUnitPrecision(
            ensureSeparator(initialValue.toString(), usesComma),
            unitDivisor(currencyConfig.currency),
            usesComma
          )
        ),
        currencyConfig
      )
    : '';

  const initialUnformattedValue = hasInitialValue
    ? truncateToSubUnitPrecision(
        ensureSeparator(initialValue.toString(), usesComma),
        unitDivisor(currencyConfig.currency),
        usesComma
      )
    : '';

  const [formattedValue, setFormattedValue] = useState(initialFormattedValue);
  const [unformattedValue, setUnformattedValue] = useState(initialUnformattedValue);
  const [value, setValue] = useState(initialFormattedValue);
  const [usesCommaState] = useState(usesComma);

  useEffect(() => {
    const newValue =
      input.value instanceof Money ? convertMoneyToNumber(input.value) : defaultValue;
    const hasNewValue = typeof newValue === 'number' && !isNaN(newValue);
    const newFormattedValue = hasNewValue
      ? intl.formatNumber(
          ensureDotSeparator(
            truncateToSubUnitPrecision(
              ensureSeparator(newValue.toString(), usesComma),
              unitDivisor(currencyConfig.currency),
              usesComma
            )
          ),
          currencyConfig
        )
      : '';
    const newUnformattedValue = hasNewValue
      ? truncateToSubUnitPrecision(
          ensureSeparator(newValue.toString(), usesComma),
          unitDivisor(currencyConfig.currency),
          usesComma
        )
      : '';

    setFormattedValue(newFormattedValue);
    setUnformattedValue(newUnformattedValue);
    if (checkCase) setValue(newFormattedValue);
  }, [input.value]);

  const updateValues = useCallback(
    event => {
      try {
        const targetValue = event.target.value.trim();
        const isEmptyString = targetValue === '';
        const valueOrZero = isEmptyString ? '0' : targetValue;

        const targetDecimalValue = isEmptyString
          ? null
          : new Decimal(ensureDotSeparator(targetValue));

        const isSafeValue =
          isEmptyString || (targetDecimalValue.isPositive() && isSafeNumber(targetDecimalValue));
        if (!isSafeValue) {
          throw new Error(`Unsafe money value: ${targetValue}`);
        }

        const truncatedValueString = truncateToSubUnitPrecision(
          valueOrZero,
          unitDivisor(currencyConfig.currency),
          usesCommaState
        );
        const unformattedValue = !isEmptyString ? truncatedValueString : '';
        const formattedValue = !isEmptyString
          ? intl.formatNumber(ensureDotSeparator(truncatedValueString), currencyConfig)
          : '';

        setFormattedValue(formattedValue);
        setUnformattedValue(unformattedValue);
        setValue(formattedValue);

        return { formattedValue, value: unformattedValue, unformattedValue };
      } catch (e) {
        return { formattedValue, unformattedValue, value };
      }
    },
    [currencyConfig, intl, usesCommaState]
  );

  const onInputChange = event => {
    event.preventDefault();
    event.stopPropagation();
    const { unformattedValue } = updateValues(event);
    const price = getPrice(ensureDotSeparator(unformattedValue), currencyConfig);
    setValue(price.amount / 100);
    input.onChange(price);
  };

  const onInputBlur = event => {
    event.preventDefault();
    event.stopPropagation();
    const price = getPrice(ensureDotSeparator(unformattedValue), currencyConfig);
    input.onBlur(price);
    setValue(formattedValue);
  };

  const onInputFocus = event => {
    event.preventDefault();
    event.stopPropagation();
    const price = getPrice(ensureDotSeparator(unformattedValue), currencyConfig);
    input.onFocus(price);
    setValue(unformattedValue);
  };

  const placeholderText = placeholder || intl.formatNumber(defaultValue, currencyConfig);
  return (
    <input
      className={className}
      {...allowedInputProps(props)}
      value={value}
      onChange={onInputChange}
      onBlur={onInputBlur}
      onFocus={onInputFocus}
      type="text"
      placeholder={placeholderText}
    />
  );
};

CurrencyInputComponent.defaultProps = {
  className: null,
  defaultValue: null,
  input: null,
  placeholder: null,
};

CurrencyInputComponent.propTypes = {
  className: string,
  currencyConfig: propTypes.currencyConfig.isRequired,
  defaultValue: number,
  isrerender: number,
  intl: intlShape.isRequired,
  input: shape({
    value: oneOfType([string, propTypes.money]),
    onBlur: func,
    onChange: func.isRequired,
    onFocus: func,
  }).isRequired,
  placeholder: string,
};

export const CurrencyInput = injectIntl(CurrencyInputComponent);

const FieldCurrencyInputComponent = props => {
  const {
    rootClassName,
    className,
    id,
    label,
    input,
    meta,
    hideErrorMessage,
    isLabelIcon,
    ...rest
  } = props;

  if (label && !id) {
    throw new Error('id required when a label is given');
  }

  const { valid, invalid, touched, error } = meta;
  const hasError = touched && invalid && error;

  const inputClasses = classNames(css.input, {
    [css.inputSuccess]: valid,
    [css.inputError]: hasError,
  });

  const inputProps = { className: inputClasses, id, input, ...rest };
  const classes = classNames(rootClassName, className);
  return (
    <div className={classes}>
      {isLabelIcon ? (
        <div className={css.labelWrapper}>
          {isLabelIcon} {label ? <label htmlFor={id}>{label}</label> : null}
        </div>
      ) : label ? (
        <label htmlFor={id}>{label}</label>
      ) : null}
      <CurrencyInput {...inputProps} />
      {hideErrorMessage ? null : <ValidationError fieldMeta={meta} />}
    </div>
  );
};

FieldCurrencyInputComponent.defaultProps = {
  rootClassName: null,
  className: null,
  id: null,
  label: null,
  hideErrorMessage: false,
};

FieldCurrencyInputComponent.propTypes = {
  rootClassName: string,
  className: string,
  id: string,
  label: string,
  hideErrorMessage: bool,
  input: object.isRequired,
  meta: object.isRequired,
};

const FieldCurrencyInput1 = props => {
  return <Field component={FieldCurrencyInputComponent} {...props} />;
};

export default FieldCurrencyInput1;
